import { useQueryClient } from "@tanstack/react-query";
import { InfiniteData } from "@tanstack/react-query";
import { SuccessFetchListResponse } from "@/services/api";
import { Module, API_CACHE_NAME } from "@/constants";
import { mergeById } from "@/lib/utils";

export type Item = Record<string, unknown> & { id: unknown };

// Function to invalidate service worker cache for API endpoints
const invalidateServiceWorkerCache = async (module: Module) => {
  if ("serviceWorker" in navigator && "caches" in window) {
    try {
      const cache = await caches.open(API_CACHE_NAME);
      const keys = await cache.keys();

      // Find and delete cache entries that match the module's API endpoints
      const keysToDelete = keys.filter((request) => {
        const url = new URL(request.url);
        // Match endpoints that contain the module name (e.g., /networks, /clients, etc.)
        return url.pathname.includes(module.toLowerCase());
      });

      await Promise.all(keysToDelete.map((key) => cache.delete(key)));
    } catch (error) {
      console.warn("Failed to invalidate service worker cache:", error);
    }
  }
};

const mergeItems = (
  existingData: { items: Item[]; count: number },
  updatedItems: Item[],
): Item[] => {
  return mergeById(existingData.items, updatedItems);
};

export const useQueryCacheUpdater = (module: Module) => {
  const queryClient = useQueryClient();

  const updateQueryDataForExistingItem = async (
    data: Record<string, unknown>,
  ) => {
    queryClient.setQueryData(
      [module, data.id],
      (oldData: Record<string, unknown>) => ({
        ...oldData,
        ...data,
      }),
    );
    queryClient.setQueryData(
      [module],
      (
        oldData: InfiniteData<
          SuccessFetchListResponse<Record<string, unknown>>
        >,
      ) => {
        if (oldData?.pages) {
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              items: page.items.map((item: Record<string, unknown>) =>
                item.id === data.id ? { ...item, ...data } : item,
              ),
            })),
          };
        }
        return oldData;
      },
    );

    // Invalidate service worker cache for this module
    await invalidateServiceWorkerCache(module);
  };

  const updateInfiniteQueryDataWithList = async (data: Item[]) => {
    queryClient.setQueryData(
      [module],
      (oldData: InfiniteData<SuccessFetchListResponse<Item>>) => {
        const flatItems = (oldData?.pages.flatMap((page) => page.items) ??
          []) as Item[];
        const items = mergeItems(
          {
            items: flatItems,
            count: flatItems.length,
          },
          data,
        );
        return {
          pages: [
            {
              items,
              count: items.length,
            },
          ],
          pageParams: [],
        };
      },
    );

    // Invalidate service worker cache for this module
    await invalidateServiceWorkerCache(module);
  };

  const updateQueryDataWithList = async (data: Item[]) => {
    queryClient.setQueryData(
      [module],
      (oldData: SuccessFetchListResponse<Item>) => {
        const items = mergeItems(oldData, data);

        return {
          items,
          count: items.length,
        };
      },
    );

    // Invalidate service worker cache for this module
    await invalidateServiceWorkerCache(module);
  };

  const invalidateListQuery = async () => {
    queryClient.invalidateQueries({
      queryKey: [module],
    });

    // Also invalidate service worker cache
    await invalidateServiceWorkerCache(module);
  };

  return {
    updateQueryDataWithList,
    updateInfiniteQueryDataWithList,
    updateQueryDataForExistingItem,
    invalidateListQuery,
  };
};
